import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';

import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import { groupBy, makeSequence, uniqBy } from 'dpl/shared/utils';
import Button from 'dpl/common/design_system/Button';
import BreederProfileLitterPuppyCard from 'dpl/components/BreederProfile/Show/BreederProfileLitterPuppyCard';
import useToggle from 'dpl/shared/hooks/useToggle';
import FilterPills from 'dpl/common/components/FilterPills';

import useLitters from '../../hooks/useLitters';

const COLLAPSED_PUPPIES_COUNT = 5;
const RESULT_GROUP_SIZE = 4;

export function PuppiesListItem({
  allPuppies,
  breederProfileId,
  position,
  puppy,
  viewFeatureLocation
}) {
  const trackInView = useCallback(() => {
    if (position % RESULT_GROUP_SIZE === 1) {
      const idx = position - 1;
      const resultGroup = allPuppies.slice(idx, idx + RESULT_GROUP_SIZE);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.SEARCH_RESULT_GROUP_VIEWED_BY_USER,
        {
          view: ANALYTICS_VIEWS.BREEDER_PROFILE,
          view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
          view_feature_location: viewFeatureLocation,
          results: resultGroup.map((result, puppyIdx) => ({
            dog_id: result.id,
            puppy_name: result.name,
            breed_id: result.breed_id,
            breeder_profile_id: breederProfileId,
            position: position + puppyIdx
          }))
        }
      );
    }
  }, [position, allPuppies, breederProfileId]);

  return (
    <li>
      <InView
        triggerOnce
        onChange={inView => {
          if (inView) trackInView();
        }}
      >
        <BreederProfileLitterPuppyCard
          puppy={puppy}
          breederProfileId={breederProfileId}
          analytics={{
            position,
            view_feature_location: viewFeatureLocation
          }}
        />
      </InView>
    </li>
  );
}

PuppiesListItem.propTypes = {
  allPuppies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  breederProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  position: PropTypes.number.isRequired,
  puppy: PropTypes.shape({}).isRequired,
  viewFeatureLocation: PropTypes.string.isRequired
};

export default function PuppiesList() {
  const { breederProfileId } = useSelector(({ server }) => server);
  const { isFetchingCurrentLitters, sortedAvailablePuppies } = useLitters();
  const [isCollapsed, toggleIsCollapsed] = useToggle(true);
  const [selectedFilterIdx, setSelectedFilterIdx] = useState(0);

  if (isFetchingCurrentLitters) {
    return (
      <div className="PuppiesListSkeleton container-xxl flex flex-column gap-24 pb8">
        <SkeletonBlock className="font-24 lh-title" height="1em" width="8em" />
        <div className="flex gap-8 font-16">
          {makeSequence(2).map(index => (
            <SkeletonBlock
              key={index}
              className="br-pill"
              height="3em"
              width="10em"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-24">
          {makeSequence(COLLAPSED_PUPPIES_COUNT).map(index => (
            <SkeletonBlock key={index} height="340px" />
          ))}
        </div>
      </div>
    );
  }

  if (sortedAvailablePuppies.length === 0) {
    return null;
  }

  const dogsByBreed = groupBy(sortedAvailablePuppies, 'breed_name');
  const filteredDogs =
    dogsByBreed[Object.keys(dogsByBreed)[selectedFilterIdx]] || [];
  const breedFilters = uniqBy(sortedAvailablePuppies, 'breed_name').map(dog => {
    return {
      label: dog.breed_name,
      photo_url: dog.breed_cover_photo
    };
  });

  const isCollapsible = filteredDogs.length > COLLAPSED_PUPPIES_COUNT;

  const listings = isCollapsed
    ? filteredDogs.slice(0, COLLAPSED_PUPPIES_COUNT)
    : filteredDogs;

  function handleBreedClick(idx) {
    setSelectedFilterIdx(idx);
  }

  return (
    <div className="PuppiesList container-xxl flex flex-column gap-24 mv8">
      <h3 className="font-24 lh-title">Available puppies</h3>
      {breedFilters.length > 1 && (
        <FilterPills
          items={breedFilters}
          onFilterClick={handleBreedClick}
          selectedFilterIdx={selectedFilterIdx}
        />
      )}
      <ul className="grid grid-cols-1 gap-24">
        {listings.map((puppy, idx) => (
          <PuppiesListItem
            key={puppy.id}
            position={idx + 1}
            allPuppies={sortedAvailablePuppies}
            puppy={puppy}
            breederProfileId={breederProfileId}
            viewFeatureLocation={
              ANALYTICS_VIEW_FEATURE_LOCATIONS.AVAILABLE_PUPPIES
            }
          />
        ))}
      </ul>

      {isCollapsible && (
        <Button
          className="font-16 stone-700 w-100 mt4"
          variant="outline"
          size="default"
          onClick={toggleIsCollapsed}
        >
          {isCollapsed ? 'See more puppies' : 'See less puppies'}
        </Button>
      )}
    </div>
  );
}
